import { useCallback, useRef } from 'react';

type TypeSize = { width: number; height: number };
type TypePosition = { x: number; y: number };

type TypeResizeEdge = 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

type TypeUseEdgeResizeOptions = {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
};

type TypeSnapshot = {
  edge: TypeResizeEdge;
  pointerX: number;
  pointerY: number;
  startWidth: number;
  startHeight: number;
  startX: number;
  startY: number;
};

const EDGE_SIZE = 8;

const CURSOR_MAP: Record<TypeResizeEdge, string> = {
  top: 'ns-resize',
  bottom: 'ns-resize',
  left: 'ew-resize',
  right: 'ew-resize',
  'top-left': 'nwse-resize',
  'bottom-right': 'nwse-resize',
  'top-right': 'nesw-resize',
  'bottom-left': 'nesw-resize',
};

/**
 * 패널 가장자리 드래그로 리사이즈
 * - 4변 + 4꼭짓점 = 8방향
 * - min/max 크기 제약 + viewport 경계 제한
 */
export const useEdgeResize = (
  size: TypeSize,
  position: TypePosition,
  onSizeChange: (size: TypeSize) => void,
  onPositionChange: (pos: TypePosition) => void,
  options: TypeUseEdgeResizeOptions
) => {
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const positionRef = useRef(position);
  positionRef.current = position;

  const snapshotRef = useRef<TypeSnapshot | null>(null);

  const { minWidth, maxWidth, minHeight, maxHeight } = options;

  const clampWidth = useCallback((w: number) => Math.max(minWidth, Math.min(maxWidth, w)), [minWidth, maxWidth]);
  const clampHeight = useCallback((h: number) => Math.max(minHeight, Math.min(maxHeight, h)), [minHeight, maxHeight]);

  const detectEdge = useCallback((e: React.PointerEvent): TypeResizeEdge | null => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const isLeft = x < EDGE_SIZE;
    const isRight = x > rect.width - EDGE_SIZE;
    const isTop = y < EDGE_SIZE;
    const isBottom = y > rect.height - EDGE_SIZE;

    if (isTop && isLeft) return 'top-left';
    if (isTop && isRight) return 'top-right';
    if (isBottom && isLeft) return 'bottom-left';
    if (isBottom && isRight) return 'bottom-right';
    if (isTop) return 'top';
    if (isBottom) return 'bottom';
    if (isLeft) return 'left';
    if (isRight) return 'right';
    return null;
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const edge = detectEdge(e);
      if (!edge) return;

      // drag 훅에 resize 우선임을 알림
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);

      const s = sizeRef.current;
      const p = positionRef.current;
      snapshotRef.current = {
        edge,
        pointerX: e.clientX,
        pointerY: e.clientY,
        startWidth: s.width,
        startHeight: s.height,
        startX: p.x,
        startY: p.y,
      };
    },
    [detectEdge]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      // 리사이즈 중이 아니면 커서만 업데이트
      if (!snapshotRef.current) {
        const edge = detectEdge(e);
        (e.currentTarget as HTMLElement).style.cursor = edge ? CURSOR_MAP[edge] : '';
        return;
      }

      const snap = snapshotRef.current;
      const dx = e.clientX - snap.pointerX;
      const dy = e.clientY - snap.pointerY;

      let nextWidth = snap.startWidth;
      let nextHeight = snap.startHeight;
      let nextX = snap.startX;
      let nextY = snap.startY;

      const affectsRight = snap.edge.includes('right');
      const affectsLeft = snap.edge.includes('left');
      const affectsBottom = snap.edge.includes('bottom');
      const affectsTop = snap.edge.includes('top');

      if (affectsRight) {
        nextWidth = clampWidth(snap.startWidth + dx);
        nextWidth = Math.min(nextWidth, window.innerWidth - nextX);
      }
      if (affectsLeft) {
        const rawWidth = snap.startWidth - dx;
        nextWidth = clampWidth(rawWidth);
        nextX = snap.startX + (snap.startWidth - nextWidth);
        if (nextX < 0) {
          nextWidth = nextWidth + nextX;
          nextX = 0;
        }
      }
      if (affectsBottom) {
        nextHeight = clampHeight(snap.startHeight + dy);
        nextHeight = Math.min(nextHeight, window.innerHeight - nextY);
      }
      if (affectsTop) {
        const rawHeight = snap.startHeight - dy;
        nextHeight = clampHeight(rawHeight);
        nextY = snap.startY + (snap.startHeight - nextHeight);
        if (nextY < 0) {
          nextHeight = nextHeight + nextY;
          nextY = 0;
        }
      }

      onSizeChange({ width: nextWidth, height: nextHeight });
      onPositionChange({ x: nextX, y: nextY });
    },
    [detectEdge, onSizeChange, onPositionChange, clampWidth, clampHeight]
  );

  const handlePointerUp = useCallback(() => {
    snapshotRef.current = null;
  }, []);

  const handleLostPointerCapture = useCallback(() => {
    snapshotRef.current = null;
  }, []);

  const handlePointerLeave = useCallback((e: React.PointerEvent) => {
    if (!snapshotRef.current) {
      (e.currentTarget as HTMLElement).style.cursor = '';
    }
  }, []);

  return {
    resizeProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onLostPointerCapture: handleLostPointerCapture,
      onPointerLeave: handlePointerLeave,
    },
  };
};
